import React from 'react';
import { Box, CardHeader, Grid2 as Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import HighlighterHeader, { HighlighterBackground } from './HighlighterHeader';
import BraintreeDropInCard from './BraintreeDropInCard';
import PaymentSummary from './PaymentSummary';
import { PlanSummary } from '../types';
import CardWithBoxShadow from '../../common/components/CardWithBoxShadow';
import SecondaryButton from '../../common/components/SecondaryButton';
import TertiaryButton from '../../common/components/TertiaryButton';

interface Props {
    maxAthleteCount: number;
    initialPlanSummary: PlanSummary;
    clientToken: string | null;
    isLoading: boolean;
    setBraintreeInstance: React.Dispatch<any>;
    handleReviewPurchase: () => Promise<void>;
    handleBack: () => void;
    isReviewPurchaseLoading: boolean;
    hasError: boolean;
    errorMessage: string | null;
    clearBraintreeErrorMessage: () => void;
}

export default function PaymentPage({
    maxAthleteCount,
    initialPlanSummary,
    handleReviewPurchase,
    clientToken,
    isLoading,
    setBraintreeInstance,
    handleBack,
    isReviewPurchaseLoading,
    hasError,
    errorMessage,
    clearBraintreeErrorMessage
}: Props) {
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box marginX={4}>
            <HighlighterHeader
                title="Payment"
                highlighterBackground={HighlighterBackground.PAYMENT}
                width={isSmallScreen ? 154 : 200}
            />
            <Box display="flex" flex={1} justifyContent="center">
                <Grid
                    display="flex"
                    flex={1}
                    container
                    spacing={2}
                    sx={{ maxWidth: 1200, flexDirection: isMediumScreen ? 'column-reverse' : null }}
                >
                    <Grid size={{ xs: 12, md: 6 }}>
                        <CardWithBoxShadow>
                            <CardHeader
                                title={
                                    <Typography variant="h5" marginTop={2} marginX={2}>
                                        Card Details
                                    </Typography>
                                }
                            />
                            <BraintreeDropInCard
                                isLoading={isLoading}
                                clientToken={clientToken}
                                setBraintreeInstance={setBraintreeInstance}
                                handleClose={() => ({})}
                                paymentFailed={false}
                                paymentSuccess={false}
                                clearErrorMessage={clearBraintreeErrorMessage}
                                removeHorizontalPadding={false}
                            />
                            {hasError && (
                                <Box className="outstanding-balance padding-desktop padding-bottom-desktop">
                                    <Typography>{errorMessage}</Typography>
                                </Box>
                            )}
                            <Grid
                                container
                                display="flex"
                                flex={1}
                                spacing={2}
                                sx={{
                                    flexDirection: isMediumScreen ? 'column-reverse' : null,
                                    margin: '0 32px 32px'
                                }}
                            >
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <TertiaryButton sx={{ width: isMediumScreen ? '100%' : null }} onClick={handleBack}>
                                        Back
                                    </TertiaryButton>
                                </Grid>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <SecondaryButton
                                        onClick={handleReviewPurchase}
                                        loading={isReviewPurchaseLoading}
                                        sx={{ width: '100%' }}
                                    >
                                        Continue
                                    </SecondaryButton>
                                </Grid>
                            </Grid>
                        </CardWithBoxShadow>
                    </Grid>
                    <Grid display="block" flex={1} container spacing={2} size={{ xs: 12, md: 6 }}>
                        <CardWithBoxShadow>
                            <Box paddingY={isSmallScreen ? 3 : 4} paddingX={isSmallScreen ? 3 : 5}>
                                <PaymentSummary
                                    maxAthleteCount={maxAthleteCount}
                                    planSummary={initialPlanSummary}
                                    showHeader={true}
                                />
                            </Box>
                        </CardWithBoxShadow>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
